import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState } from 'react';
import { PipecatClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport, ProtobufFrameSerializer } from '@pipecat-ai/websocket-transport';
import StitchLayout from '../../components/Crm/CrmLayout';

type SimStatus = 'idle' | 'requesting' | 'connecting' | 'connected' | 'ended' | 'error';

const DOT: Record<SimStatus, string> = {
    idle:       '#9ca3af',
    requesting: '#f59e0b',
    connecting: '#3b82f6',
    connected:  '#22c55e',
    ended:      '#9ca3af',
    error:      '#ef4444',
};

const LABEL: Record<SimStatus, string> = {
    idle:       'Ready',
    requesting: 'Requesting…',
    connecting: 'Connecting…',
    connected:  'Live',
    ended:      'Ended',
    error:      'Error',
};

/* ─── CSS injected once ─────────────────────────────────────────── */
const STYLES = `
@keyframes sahara-ring {
    0%   { transform: scale(0.6); opacity: 0.5; }
    100% { transform: scale(2.6); opacity: 0; }
}
@keyframes sahara-ring2 {
    0%   { transform: scale(0.6); opacity: 0.35; }
    100% { transform: scale(2.2); opacity: 0; }
}
@keyframes sahara-bar {
    0%, 100% { height: 6px;  }
    50%       { height: 28px; }
}
@keyframes sahara-fade-bg {
    from { opacity: 0; }
    to   { opacity: 1; }
}
@keyframes sahara-dot-live {
    0%, 100% { box-shadow: 0 0 0 0   rgba(34,197,94,0.6); }
    50%       { box-shadow: 0 0 0 7px rgba(34,197,94,0);   }
}
`;

const BAR_DELAYS = [0, 0.15, 0.3, 0.1, 0.25, 0.05, 0.2];

const SaharaWebRTCSimulator: React.FC = () => {
    const [phone, setPhone]       = useState('');
    const [status, setStatus]     = useState<SimStatus>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [client, setClient]     = useState<any>(null);
    const [isBotSpeaking, setIsBotSpeaking] = useState(false);
    const abortRef                = React.useRef(false);

    const isBusy   = status === 'requesting' || status === 'connecting';
    const isActive = status === 'connected';
    const canCall  = !isBusy && !isActive;
    const isDark   = isActive || isBusy;

    const startCall = async () => {
        if (!phone.trim()) { setStatus('error'); setErrorMsg('Enter a phone number'); return; }
        abortRef.current = false;
        setStatus('requesting');
        setErrorMsg('');

        try {
            const res = await axiosInstance.post(apis.SAHARA_INBOUND_INITIATE_WEBRTC, { phone_number: phone });
            if (abortRef.current) return;

            const { status: s, ws_url, message } = res.data;
            if (s !== 'success' || !ws_url) throw new Error(message || 'No ws_url returned');

            setStatus('connecting');

            const transport = new WebSocketTransport({ serializer: new ProtobufFrameSerializer() });
            const pc = new PipecatClient({
                transport,
                enableMic: true,
                enableCam: false,
                callbacks: {
                    onConnected: () => {
                        if (abortRef.current) { pc.disconnect().catch(() => {}); return; }
                        setStatus('connected');
                    },
                    onDisconnected: () => { setStatus('ended'); setClient(null); setIsBotSpeaking(false); },
                    onError: (err: any) => {
                        if (!abortRef.current) { setStatus('error'); setErrorMsg(err?.message || 'Connection error'); }
                    },
                },
            });

            pc.on(RTVIEvent.BotStartedSpeaking, () => setIsBotSpeaking(true));
            pc.on(RTVIEvent.BotStoppedSpeaking, () => setIsBotSpeaking(false));

            if (abortRef.current) return;
            await pc.connect({ wsUrl: ws_url });
            if (abortRef.current) { try { await pc.disconnect(); } catch (_) {} return; }
            setClient(pc);

        } catch (err: any) {
            if (!abortRef.current) { setStatus('error'); setErrorMsg(err.message || 'Failed to start call'); }
        }
    };

    const endCall = async () => {
        abortRef.current = true;
        setStatus('ended');
        setClient(null);
        setIsBotSpeaking(false);
        try { await axiosInstance.post(`${apis.SAHARA_INBOUND_DISCONNECT_WEBRTC}?phone=${phone}`, { phone_number: phone }); }
        catch (_) {}
    };

    const reset = () => { setStatus('idle'); setErrorMsg(''); };

    return (
        <StitchLayout>
            {/* Inject keyframes once */}
            <style>{STYLES}</style>

            {/* Full-bleed background overlay when call is active */}
            <div style={{
                position:   'fixed',
                inset:       0,
                left:        256,   /* sidebar width */
                top:         64,    /* header height */
                background:  isDark
                    ? 'linear-gradient(135deg, #0d0d1a 0%, #120d2e 50%, #0a1628 100%)'
                    : 'transparent',
                transition: 'background 0.6s ease',
                pointerEvents: 'none',
                zIndex:      0,
                animation:   isDark ? 'sahara-fade-bg 0.5s ease forwards' : 'none',
            }} />

            {/* Animated rings — only when connected */}
            {isActive && (
                <div style={{
                    position:   'fixed',
                    top:        '50%',
                    left:       'calc(50% + 128px)',  /* account for sidebar */
                    transform:  'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex:      1,
                }}>
                    {/* Ring 1 */}
                    <div style={{
                        position:     'absolute',
                        width:         200,
                        height:        200,
                        borderRadius: '50%',
                        border:       '1.5px solid rgba(124,58,237,0.5)',
                        top:           '50%',
                        left:          '50%',
                        transform:    'translate(-50%,-50%) scale(0.6)',
                        animation:    'sahara-ring 2.4s ease-out infinite',
                    }} />
                    {/* Ring 2 */}
                    <div style={{
                        position:     'absolute',
                        width:         200,
                        height:        200,
                        borderRadius: '50%',
                        border:       '1.5px solid rgba(99,102,241,0.4)',
                        top:           '50%',
                        left:          '50%',
                        transform:    'translate(-50%,-50%) scale(0.6)',
                        animation:    'sahara-ring 2.4s ease-out 0.8s infinite',
                    }} />
                    {/* Ring 3 */}
                    <div style={{
                        position:     'absolute',
                        width:         200,
                        height:        200,
                        borderRadius: '50%',
                        border:       '1px solid rgba(139,92,246,0.25)',
                        top:           '50%',
                        left:          '50%',
                        transform:    'translate(-50%,-50%) scale(0.6)',
                        animation:    'sahara-ring 2.4s ease-out 1.6s infinite',
                    }} />
                </div>
            )}

            {/* Page content */}
            <div style={{
                maxWidth:        400,
                margin:          '72px auto',
                display:         'flex',
                flexDirection:   'column',
                gap:              24,
                position:        'relative',
                zIndex:           2,
            }}>
                {/* Title */}
                <div>
                    <h1 style={{
                        fontSize:    20,
                        fontWeight:  700,
                        color:       isDark ? '#f9fafb' : '#111827',
                        margin:       0,
                        marginBottom: 4,
                        transition:  'color 0.4s',
                    }}>
                        Sahara Call Simulator
                    </h1>
                    <p style={{
                        fontSize:   13,
                        color:      isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
                        margin:      0,
                        transition: 'color 0.4s',
                    }}>
                        Inbound WebRTC · Sahara AI
                    </p>
                </div>

                {/* Card */}
                <div style={{
                    background:   isDark
                        ? 'rgba(255,255,255,0.06)'
                        : '#fff',
                    border:       `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                    borderRadius:  14,
                    padding:      '28px 24px',
                    display:      'flex',
                    flexDirection: 'column',
                    gap:           16,
                    backdropFilter: isDark ? 'blur(12px)' : 'none',
                    transition:   'background 0.4s, border-color 0.4s',
                    boxShadow:    isDark ? '0 8px 40px rgba(0,0,0,0.4)' : 'none',
                }}>
                    {/* Phone input */}
                    <input
                        type="tel"
                        placeholder="Phone number"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && canCall && startCall()}
                        disabled={isBusy || isActive}
                        style={{
                            width:        '100%',
                            padding:      '11px 14px',
                            fontSize:      15,
                            fontFamily:   'monospace',
                            border:       `1.5px solid ${
                                status === 'error'
                                    ? '#fca5a5'
                                    : isDark
                                    ? 'rgba(255,255,255,0.15)'
                                    : '#e5e7eb'
                            }`,
                            borderRadius:  8,
                            outline:      'none',
                            background:   isDark
                                ? 'rgba(255,255,255,0.07)'
                                : (isBusy || isActive) ? '#f9fafb' : '#fff',
                            color:        isDark ? '#f9fafb' : '#111827',
                            boxSizing:    'border-box',
                            transition:   'border-color 0.3s, background 0.3s, color 0.3s',
                        }}
                    />

                    {/* CTAs */}
                    {canCall && (
                        <button
                            onClick={status === 'ended' || status === 'error' ? reset : startCall}
                            style={{
                                width:        '100%',
                                padding:      '11px 0',
                                borderRadius:  8,
                                border:       'none',
                                cursor:       'pointer',
                                fontSize:      14,
                                fontWeight:    600,
                                background:   status === 'ended' ? '#f3f4f6' : '#7c3aed',
                                color:        status === 'ended' ? '#374151' : '#fff',
                                transition:   'background 0.15s',
                            }}
                        >
                            {status === 'ended' ? 'New Call' : 'Start Call'}
                        </button>
                    )}

                    {isBusy && (
                        <button
                            onClick={endCall}
                            style={{
                                width:       '100%',
                                padding:     '11px 0',
                                borderRadius: 8,
                                border:      'none',
                                cursor:      'pointer',
                                fontSize:     14,
                                fontWeight:   600,
                                background:  'rgba(239,68,68,0.15)',
                                color:       '#ef4444',
                            }}
                        >
                            Cancel
                        </button>
                    )}

                    {isActive && (
                        <button
                            onClick={endCall}
                            style={{
                                width:       '100%',
                                padding:     '11px 0',
                                borderRadius: 8,
                                border:      'none',
                                cursor:      'pointer',
                                fontSize:     14,
                                fontWeight:   600,
                                background:  '#dc2626',
                                color:       '#fff',
                            }}
                        >
                            End Call
                        </button>
                    )}

                    {status === 'error' && errorMsg && (
                        <p style={{ margin: 0, fontSize: 12, color: '#ef4444', textAlign: 'center' }}>
                            {errorMsg}
                        </p>
                    )}
                </div>

                {/* Status row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                    <span style={{
                        display:      'inline-block',
                        width:         7,
                        height:        7,
                        borderRadius: '50%',
                        background:   DOT[status],
                        flexShrink:    0,
                        animation:    isActive ? 'sahara-dot-live 1.4s ease infinite' : 'none',
                    }} />
                    <span style={{
                        fontSize:   12,
                        color:      isDark ? 'rgba(255,255,255,0.45)' : '#9ca3af',
                        fontWeight:  500,
                        transition: 'color 0.4s',
                    }}>
                        {LABEL[status]}
                    </span>

                    {/* Audio wave bars — visible only when bot is speaking */}
                    {isActive && (
                        <div style={{
                            display:    'flex',
                            alignItems: 'center',
                            gap:         3,
                            marginLeft:  4,
                            height:      28,
                            opacity:    isBotSpeaking ? 1 : 0.25,
                            transition: 'opacity 0.3s',
                        }}>
                            {BAR_DELAYS.map((delay, i) => (
                                <div key={i} style={{
                                    width:        3,
                                    height:       6,
                                    borderRadius: 2,
                                    background:   '#a78bfa',
                                    animation:    `sahara-bar ${0.7 + (i % 3) * 0.15}s ease-in-out ${delay}s infinite`,
                                }} />
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </StitchLayout>
    );
};

export default SaharaWebRTCSimulator;
